import React, { useRef, useLayoutEffect, useCallback, useState } from 'react';
import { StickyBreadcrumbBar } from './comps';
import { TreeTable, TreeTableSortEvent, TreeTableEvent } from 'primereact/treetable';
import { Column } from 'primereact/column';
import { useMemo } from 'react';
import { TextOverflowTooltip } from 'src/common';
import { Header } from './comps/Header';
import { OrgAvatar, SortIcon, Tdm } from './comps';
import { GTColumnSelectAnchor } from 'src/components/GenericTable/comps';
import useLocalStorage from 'src/hooks/useLocalStorage';
import useWorkspaceSwitch from 'src/hooks/useWorkspaceSwitch';
import { has, omit } from 'lodash';
import { ColumnHeader, Container, TableContainer, StickyHeader } from './styled';
import { MemberAccess } from 'src/graphql/_generated';
import { TableIds } from 'src/api/types/misc';
import { FlatOrgData, FlatOrgTreeTableNode } from 'src/utils/treeNode';
import { getBusinessTypeLabel, getMemberAccessLabel, getOrganisationTypeLabel, getPlanTypeLabel } from 'src/api/types';
import { useBreadcrumbPath } from './useBreadcrumbPath';
import { useOrgTableData } from './useOrgTableData';
import { useAuth } from 'src/state/auth';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { LSKEY_INITAIL_URL } from 'src/utils/auth';

interface OrgTableProps {
  data: FlatOrgData[];
  from?: string;
}

const OrgTable: React.FC<OrgTableProps> = ({ data, from }) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { profile, isPlatformAdmin } = useAuth();
  const { switchOrg, isLoading: isLoadingSwitch } = useWorkspaceSwitch();

  const [visibleColumns, setVisibleColumns] = useLocalStorage<string[]>(`${TableIds.rootHerarchy}-visible-columns`, [
    'account-type',
    'environment-type',
    'access',
    'workspace-restriction',
    'parent-org',
  ]);

  const { filteredData, expandedKeys, setExpandedKeys, sortField, sortOrder, onSort, parentOrgNameById, levelById } =
    useOrgTableData({ data, from, searchTerm });

  // Check if there's only one root node - used to skip highlighting direct children of single root
  const hasSingleRoot = filteredData.length === 1;

  const canOpenWorkspace = useCallback(
    (nodeData: FlatOrgTreeTableNode['data']) => {
      if (nodeData.recordType !== 'W') return false;

      const hasPendingRequest = profile?.user.requests.some((el) => el.workspaceId === nodeData.id);
      if (hasPendingRequest) return false;

      const hasInvite = profile?.user.invites.some((el) => el.workspaceId === nodeData.id);
      if (hasInvite) return false;

      return Boolean(nodeData.actionsOnThisOrgUnit?.canAccess || isPlatformAdmin);
    },
    [profile, isPlatformAdmin],
  );

  // Flatten visible nodes in render order for data-rk attribute assignment
  const flattenVisibleNodes = useCallback(
    (nodes: FlatOrgTreeTableNode[], expanded: Record<string, boolean>): string[] => {
      const result: string[] = [];
      const traverse = (nodeList: FlatOrgTreeTableNode[]) => {
        for (const node of nodeList) {
          result.push(node.key);
          if (node.children?.length && expanded[node.key]) {
            traverse(node.children);
          }
        }
      };
      traverse(nodes);
      return result;
    },
    [],
  );

  // Add data-rk attributes to rows after render for breadcrumb tracking
  useLayoutEffect(() => {
    if (!tableContainerRef.current) return;

    const tbody = tableContainerRef.current.querySelector('.p-treetable-tbody');
    if (!tbody) return;

    const rows = tbody.querySelectorAll('tr');
    const visibleKeys = flattenVisibleNodes(filteredData, expandedKeys);

    rows.forEach((row, index) => {
      if (visibleKeys[index]) {
        row.setAttribute('data-rk', visibleKeys[index]);
      }
    });
  }, [filteredData, expandedKeys, flattenVisibleNodes]);

  // Breadcrumb path hook - shows parent hierarchy when scrolled down
  const breadcrumbPath = useBreadcrumbPath({
    tableRef: tableContainerRef,
    data: filteredData,
    expandedKeys,
  });

  const columnSelectAnchor = useMemo(
    () => (
      <GTColumnSelectAnchor
        options={[
          { key: 'account-type', title: 'Account Type' },
          { key: 'parent-org', title: 'Parent Org' },
          { key: 'organisation-type', title: 'Organisation Type' },
          { key: 'business-type', title: 'Business Type' },
          { key: 'pricing-plan', title: 'Pricing Plan' },
          { key: 'access', title: 'Access' },
          { key: 'workspace-restriction', title: 'Workspace Restriction' },
          { key: 'domain', title: 'Domain' },
          { key: 'primary-contact', title: 'Primary Contact' },
        ]}
        value={visibleColumns}
        defaultOptions={['account-type', 'access', 'workspace-restriction']}
        onChange={setVisibleColumns}
        tableId={TableIds.rootHerarchy}
      />
    ),
    [visibleColumns, setVisibleColumns],
  );

  const getColumnClassName = (column: string) => {
    return visibleColumns.includes(column) ? '' : 'c-hidden';
  };

  const handleSort = (event: TreeTableSortEvent) => {
    onSort(event.sortField);
  };

  const handleRowClick = (e: TreeTableEvent) => {
    const nodeData = e.node?.data as FlatOrgTreeTableNode['data'];
    if (nodeData?.recordType === 'W' && canOpenWorkspace(nodeData) && !isLoadingSwitch) {
      window.localStorage.setItem(LSKEY_INITAIL_URL, '/risk-assessments');
      switchOrg(nodeData.id);
      mpEvent(MPEvents.WorkspaceSwitch, { workspaceID: nodeData.id });
      return;
    }

    if (e.node.children) {
      const nodeKey = e.node.key as string;
      const newExpandedKeys = has(expandedKeys, nodeKey)
        ? omit(expandedKeys, nodeKey)
        : { ...expandedKeys, [nodeKey]: true };
      setExpandedKeys(newExpandedKeys);
    }
  };

  type NodeData = { data: FlatOrgTreeTableNode['data']; rowIndex: number };

  return (
    <Container>
      <StickyHeader $stickyTop={0}>
        <Header onChange={setSearchTerm} />
      </StickyHeader>
      <StickyBreadcrumbBar path={breadcrumbPath} />
      <TableContainer ref={tableContainerRef} $stickyTop={0}>
        <TreeTable
          value={filteredData}
          onSort={handleSort}
          filterMode="strict"
          sortOrder={sortOrder}
          expandedKeys={expandedKeys}
          onToggle={(e) => setExpandedKeys(e.value)}
          rowClassName={({ children, data }) => {
            const level = levelById.get(data.id) ?? 1;
            return {
              expandable: children?.length ? true : false,
              'workspace-openable': canOpenWorkspace(data),
              // Highlight children rows, but skip if single root (would highlight entire table)
              'child-row-highlight': hasSingleRoot ? level >= 3 : level >= 2,
            };
          }}
          onRowClick={handleRowClick}
          sortIcon={() => null}
        >
          {/* Name (O,W) */}
          <Column
            field="name"
            header={
              <ColumnHeader>
                <span>Name</span>
                <SortIcon sortOrder={sortField === 'name' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            expander
            sortable
            className="c-name"
            body={({ data }: NodeData) => {
              const isWorkspace = data.recordType === 'W';
              const allowedAccess = [MemberAccess.Allowed];
              const level = levelById.get(data.id) ?? 1;

              return (
                <OrgAvatar
                  id={data.id}
                  name={data.name}
                  imgUrl={null}
                  padlock={isWorkspace && !allowedAccess.includes(data.member?.access!)}
                  canAccess={data.actionsOnThisOrgUnit?.canAccess}
                  level={level}
                  isWorkspace={isWorkspace}
                />
              );
            }}
          />

          {/* Parent Org (O,W) */}
          <Column
            header={
              <ColumnHeader>
                <span>Parent Org</span>
              </ColumnHeader>
            }
            className={getColumnClassName('parent-org')}
            body={({ data }: NodeData) => (
              <TextOverflowTooltip>{parentOrgNameById.get(data.id) || ''}</TextOverflowTooltip>
            )}
          />

          {/* Account Type, (O,W) */}
          <Column
            field="account-type"
            header={
              <ColumnHeader>
                <span>Account Type</span>
                <SortIcon sortOrder={sortField === 'account-type' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('account-type')}
            body={({ data }: NodeData) => {
              const archivedLabel = data.isArchived ? ' (Archived)' : '';
              return data.recordType === 'O' ? `Organisation${archivedLabel}` : `Workspace${archivedLabel}`;
            }}
          />

          {/* Organisation Type (O) */}
          <Column
            field="organisation-type"
            header={
              <ColumnHeader>
                <span>Organisation Type</span>
                <SortIcon sortOrder={sortField === 'organisation-type' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('organisation-type')}
            body={({ data }: NodeData) => {
              if (data.recordType === 'O') {
                return <TextOverflowTooltip>{getOrganisationTypeLabel(data.organisationType)}</TextOverflowTooltip>;
              }
              return null;
            }}
          />

          {/* Business Type (O) */}
          <Column
            field="business-type"
            header={
              <ColumnHeader>
                <span>Business Type</span>
                <SortIcon sortOrder={sortField === 'business-type' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('business-type')}
            body={({ data }: NodeData) => {
              if (data.recordType === 'O') {
                return <TextOverflowTooltip>{getBusinessTypeLabel(data.businessType)}</TextOverflowTooltip>;
              }
              return null;
            }}
          />

          {/* Pricing Plan (O) */}
          <Column
            field="pricing-plan"
            header={
              <ColumnHeader>
                <span>Pricing Plan</span>
                <SortIcon sortOrder={sortField === 'pricing-plan' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('pricing-plan')}
            body={({ data }: NodeData) => {
              if (data.recordType === 'O') {
                return <TextOverflowTooltip>{getPlanTypeLabel(data.planType)}</TextOverflowTooltip>;
              }
              return null;
            }}
          />

          {/* Access (W) */}
          <Column
            field="access"
            header={
              <ColumnHeader>
                <span>Access</span>
                <SortIcon sortOrder={sortField === 'access' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('access')}
            body={({ data }: NodeData) => {
              if (data.recordType === 'W') {
                return <TextOverflowTooltip>{getMemberAccessLabel(data.member?.access)}</TextOverflowTooltip>;
              }
              return null;
            }}
          />

          {/* Workspace Restriction (W) */}
          <Column
            field="workspace-restriction"
            header={
              <ColumnHeader>
                <span>Workspace Restriction</span>
                <SortIcon sortOrder={sortField === 'workspace-restriction' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('workspace-restriction')}
            body={({ data }: NodeData) => {
              if (data.recordType === 'W') {
                return <TextOverflowTooltip>{data.isRestricted ? 'Restricted' : 'Open'}</TextOverflowTooltip>;
              }
              return null;
            }}
          />

          {/* Domain (O,W) */}
          <Column
            field="domain"
            header={
              <ColumnHeader>
                <span>Domain</span>
                <SortIcon sortOrder={sortField === 'domain' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('domain')}
            body={({ data }: NodeData) => <TextOverflowTooltip>{data.domain?.replace(/^@/, '')}</TextOverflowTooltip>}
          />

          {/* Primary Contact (O,W) */}
          <Column
            field="primary-contact"
            header={
              <ColumnHeader>
                <span>Primary Contact</span>
                <SortIcon sortOrder={sortField === 'primary-contact' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('primary-contact')}
            body={({ data }: NodeData) => <TextOverflowTooltip>{data.primaryContact}</TextOverflowTooltip>}
          />

          {/* TDM */}
          <Column
            field=""
            header={columnSelectAnchor}
            className="c-tdm"
            body={({ data, rowIndex }: NodeData) => <Tdm data={data} rowIndex={rowIndex} />}
          />
        </TreeTable>
      </TableContainer>
    </Container>
  );
};

export default OrgTable;
